import { v4 as uuidv4 } from 'uuid';
import { useCallback } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { connect } from 'react-redux';
import { FormSpy } from 'react-final-form';
import { getIn } from 'final-form';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import {
  getActFamiliarizationNotificationsBlock,
  getActFamiliarizationNotificationsBlockId,
} from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmActFamiliarizationNotificationsForm } from 'KnmErknmPackage/MainModule/interfaces/IKnmActFamiliarizationNotificationsForm';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { Hint } from 'CorePackage/MainModule/components/Hint';
import { getFieldArrayErrorMessage } from 'CorePackage/MainModule/common/getFieldArrayErrorMessage';

import { KnmActIntroductionItem } from './KnmActIntroductionItem';
import styles from './KnmActIntroduction.module.scss';
import { IRootState } from '../../../CorePackage/MainModule/interfaces/IRootState';
import { getUserRoleIsSupervisor } from '../../../AuthPackage/MainModule/selectors/authSelectors';
import { getTitleFieldWithRedStar } from '../../../functions/getTitleFieldWithRedStar';

interface IErknmActIntroductionsProps {
  id: string;
  name: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  isPm?: boolean;
  isDecision?: boolean;
  isDisabledByDirty?: boolean;
}

interface IErknmActIntroductionsStoreProps {
  isSupervisor: boolean;
}

const ErknmActIntroductionsWithoutStore = ({
  id,
  name,
  formRef,
  isDecision,
  isPm,
  isDisabledByDirty = false,
  isSupervisor,
}: IErknmActIntroductionsProps & IErknmActIntroductionsStoreProps) => {
  const remove = formRef?.current.mutators.remove;
  const push = formRef?.current.mutators.push;
  const familiarizationNotificationsBlockId = getActFamiliarizationNotificationsBlock(id);
  const familiarizationNotificationsFieldName = `${name}.familiarizationNotifications`;

  const isDisable = isDisabledByDirty || !isSupervisor;

  const addNotify = useCallback(() => {
    if (push) {
      const addingEvent: IKnmActFamiliarizationNotificationsForm = {
        listId: uuidv4(),
        isNew: true,
        existedAtSigning: false,
        knmNoticeType: undefined,
        note: undefined,
        noticeDate: undefined,
      };

      push(familiarizationNotificationsFieldName, addingEvent);
    }
  }, [familiarizationNotificationsFieldName, push]);

  const makeRemoveNotifyHandler = (index: number, isNew: boolean) => () =>
    isNew && remove && remove(familiarizationNotificationsFieldName, index);

  return (
    <FieldBlock
      id={familiarizationNotificationsBlockId}
      className={styles.GroupMarginBottom}
      margin="top"
      label={getTitleFieldWithRedStar({
        label: 'Уведомление контролируемого лица об акте',
        field: familiarizationNotificationsFieldName,
        isPm,
        isDecision,
        isKnm: !isPm && !isDecision,
      })}
      button={
        <Button
          className="shared-btn-add"
          text="Добавить"
          variant="link"
          color="primary"
          type="button"
          onClick={addNotify}
          disabled={isDisable}
        />
      }
    >
      <>
        <FormSpy subscription={{ errors: true }}>
          {({ errors }) => {
            const errorMessage = getFieldArrayErrorMessage(getIn(errors || {}, familiarizationNotificationsFieldName));

            if (!errorMessage) {
              return null;
            }

            return <p className={`${styles.ErrorMessage} shared-input-error`}>{errorMessage}</p>;
          }}
        </FormSpy>
        <span className={styles.Message}>
          <Hint
            text="Если уведомление контролируемого лица производится через портал Госуслуги, соответствующая информация размещается и
          обновляется автоматически. Вручную указывать такие сведения не требуется."
          />
        </span>
        <FieldArray name={familiarizationNotificationsFieldName} allowNull>
          {({ fields }: FieldArrayRenderProps<IKnmActFamiliarizationNotificationsForm, HTMLElement>) => {
            if (!fields.length) {
              return null;
            }

            return fields.map((fieldName, sIndex) => {
              const fieldValue: IKnmActFamiliarizationNotificationsForm = fields.value?.[sIndex];
              const itemId = getActFamiliarizationNotificationsBlockId(id, fieldValue.listId);

              return (
                <KnmActIntroductionItem
                  fieldValue={fieldValue}
                  id={itemId}
                  key={itemId}
                  isDisabledField={isDisable || !fieldValue.isNew}
                  name={fieldName}
                  remove={makeRemoveNotifyHandler(sIndex, fieldValue.isNew)}
                  isDecision={isDecision}
                  isPm={isPm}
                />
              );
            });
          }}
        </FieldArray>
      </>
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmActIntroductionsStoreProps => ({
  isSupervisor: getUserRoleIsSupervisor(state),
});

export const KnmActIntroductions = connect(mapStateToProps)(ErknmActIntroductionsWithoutStore);
